import { Ionicons } from '@expo/vector-icons';
import messaging from '@react-native-firebase/messaging';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  Animated,
  Dimensions,
  Easing,
  I18nManager,
  Image,
  InteractionManager,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import appConfig from '../../app.json';
import AnimatedWave from '../components/AnimatedWave';
import { changeLanguage } from '../config/i18n';
import { borderRadius, colors, spacing } from '../config/theme';
import { useAuth } from '../contexts/AuthContext';
import { RootStackParamList } from '../navigation/RootNavigator';
import notificationService from '../services/notificationService';
import logger from '../utils/logger';
import { platformStorage } from '../utils/storage';

type LoginScreenNavigationProp = StackNavigationProp<RootStackParamList, 'Login'>;

export default function LoginScreen() {
  const { login, logout, setPending2FA } = useAuth();
  const navigation = useNavigation<LoginScreenNavigationProp>();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const isRTL = I18nManager.isRTL;

  // Animation values
  const logoScale = useRef(new Animated.Value(0)).current;
  const cardSlideUp = useRef(new Animated.Value(50)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const warpAnim = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const galaxyRotate = useRef(new Animated.Value(0)).current;
  const twinkleAnim = useRef(new Animated.Value(0)).current;
  const shootingStarAnim = useRef(new Animated.Value(0)).current;
  const logoPulseAnim = useRef(new Animated.Value(0)).current;
  const nebulaAnim = useRef(new Animated.Value(0)).current;
  const planetsAnim = useRef(new Animated.Value(0)).current;
  const particlesAnim = useRef(new Animated.Value(0)).current;
  const animLoopsRef = useRef<Animated.CompositeAnimation[]>([]);

  // Generate random stars for galaxy effect
  const [stars] = useState(() =>
    [...Array(80)].map((_, i) => ({
      id: i,
      top: Math.random() * 120 - 10,
      left: Math.random() * 120 - 10,
      size: Math.random() * 3 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      duration: Math.random() * 3000 + 2000,
    })),
  );

  // Generate floating planet (single, top area only)
  const [planets] = useState(() => [
    {
      id: 0,
      size: 50, // Fixed medium size
      top: 12, // Top area only (12%)
      left: 20, // Left side
      color: 'rgba(150,200,255,0.5)', // Nice blue planet
      duration: 20000,
    },
  ]);

  // Generate floating particles (reduced for cleaner look)
  const [particles] = useState(() =>
    [...Array(15)].map((_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      top: Math.random() * 100,
      left: Math.random() * 100,
      opacity: Math.random() * 0.3 + 0.1,
    })),
  );

  useEffect(() => {
    loadSavedCredentials();

    // Animations
    Animated.parallel([
      Animated.spring(logoScale, {
        toValue: 1,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.sequence([
        Animated.timing(cardOpacity, {
          toValue: 1,
          duration: 400,
          delay: 200,
          useNativeDriver: true,
        }),
        Animated.timing(cardSlideUp, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
        }),
      ]),
    ]).start();

    // Shimmer animation - very subtle
    const shimmerLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 3000,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 3000,
          useNativeDriver: true,
        }),
      ]),
    );
    shimmerLoop.start();

    // Galaxy Rotation
    const galaxyLoop = Animated.loop(
      Animated.timing(galaxyRotate, {
        toValue: 1,
        duration: 120000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    galaxyLoop.start();

    // Twinkle effect
    const twinkleLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(twinkleAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(twinkleAnim, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ]),
    );
    twinkleLoop.start();

    // Shooting Star Animation (Natural curved path)
    const runShootingStar = () => {
      shootingStarAnim.setValue(0);
      Animated.sequence([
        Animated.delay(Math.random() * 10000 + 10000), // 10-20 seconds between shooting stars
        Animated.timing(shootingStarAnim, {
          toValue: 1,
          duration: 2500, // Slower, more realistic (2.5 seconds)
          easing: Easing.bezier(0.4, 0, 0.2, 1), // Natural deceleration
          useNativeDriver: true,
        }),
      ]).start(() => runShootingStar());
    };
    runShootingStar();

    // Nebula Cloud Animation
    const nebulaLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(nebulaAnim, {
          toValue: 1,
          duration: 8000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(nebulaAnim, {
          toValue: 0,
          duration: 8000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    nebulaLoop.start();

    // Planets Orbit Animation
    const planetsLoop = Animated.loop(
      Animated.timing(planetsAnim, {
        toValue: 1,
        duration: 25000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    planetsLoop.start();

    // Floating Particles Animation
    const particlesLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(particlesAnim, {
          toValue: 1,
          duration: 6000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(particlesAnim, {
          toValue: 0,
          duration: 6000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    particlesLoop.start();

    // Logo Pulse (Breathing)
    const logoPulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(logoPulseAnim, {
          toValue: 1,
          duration: 3000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(logoPulseAnim, {
          toValue: 0,
          duration: 3000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    logoPulseLoop.start();

    animLoopsRef.current = [
      shimmerLoop,
      galaxyLoop,
      twinkleLoop,
      nebulaLoop,
      planetsLoop,
      particlesLoop,
      logoPulseLoop,
    ];

    return () => {
      animLoopsRef.current.forEach((loop) => loop.stop());
      animLoopsRef.current = [];
    };
  }, []);

  // ✅ FIX: Re-initialize animations when screen comes into focus (fixes freeze after logout)
  useFocusEffect(
    useCallback(() => {
      logger.log('🎬 LoginScreen focused - re-initializing animations');

      // Reset animation values to starting positions
      logoScale.setValue(0);
      cardSlideUp.setValue(50);
      cardOpacity.setValue(0);

      // Start entrance animations
      Animated.parallel([
        Animated.spring(logoScale, {
          toValue: 1,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }),
        Animated.spring(cardSlideUp, {
          toValue: 0,
          tension: 40,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(cardOpacity, {
          toValue: 1,
          duration: 800,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(() => {
        logger.log('✅ LoginScreen entrance animations completed');
      });

      logger.log('✅ LoginScreen animations re-initialized');

      // Cleanup: Stop animations when screen unfocuses
      return () => {
        logger.log('🛑 LoginScreen unfocused - stopping animations');
        logoScale.stopAnimation();
        cardSlideUp.stopAnimation();
        cardOpacity.stopAnimation();
        warpAnim.stopAnimation();
      };
    }, []),
  );

  const loadSavedCredentials = async () => {
    try {
      const savedEmail = await platformStorage.getItem('saved_email');
      const savedRemember = await platformStorage.getItem('remember_me');

      if (savedRemember === 'true' && savedEmail) {
        setEmail(savedEmail);
        setRememberMe(true);
        // Note: Password is never saved for security reasons
        // User must re-enter password on each login
      }
    } catch (error) {
      logger.error('loadSavedCredentials error:', error);
    }
  };

  const toggleLanguage = async () => {
    const newLang = currentLanguage === 'ar' ? 'en' : 'ar';

    try {
      // changeLanguage handles saving, RTL, and reload automatically
      await changeLanguage(newLang);
      setCurrentLanguage(newLang);
    } catch (error) {
      logger.error('Language toggle failed:', error);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ✅ Request Firebase notification permissions after login
  const requestNotificationPermissions = async () => {
    // Skip notifications on web platform (Firebase not configured for web)
    if (Platform.OS === 'web') {
      logger.log('Skipping push notifications on web platform');
      return;
    }

    try {
      // Check current authorization status
      const authStatus = await messaging().hasPermission();
      const enabled =
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL;

      if (enabled) {
        logger.log('✅ Notification permissions already granted');
        return;
      }

      // If not granted, show alert (reminder on every login)
      // ✅ Use InteractionManager to prevent presenting dialog during navigation
      InteractionManager.runAfterInteractions(() => {
        Alert.alert(
          t('notifications.permissionTitle') || 'تفعيل الإشعارات',
          t('notifications.permissionMessage') ||
            'للحصول على إشعارات فورية عن حالة الأجهزة والإنذارات، يرجى تفعيل الإشعارات.',
          [
            {
              text: t('common.cancel') || 'لاحقاً',
              style: 'cancel',
              onPress: () => {
                logger.log('⏭️ User skipped notification permission');
              },
            },
            {
              text: t('common.enable') || 'تفعيل',
              onPress: async () => {
                try {
                  const authorizationStatus = await messaging().requestPermission();

                  if (
                    authorizationStatus === messaging.AuthorizationStatus.AUTHORIZED ||
                    authorizationStatus === messaging.AuthorizationStatus.PROVISIONAL
                  ) {
                    logger.log('✅ Notification permissions granted by user');

                    // Get FCM token
                    const fcmToken = await messaging().getToken();
                    logger.log('✅ FCM Token obtained successfully');

                    // ✅ FIX: Register token with backend so server can push notifications
                    if (fcmToken) {
                      try {
                        await notificationService.updatePushToken(fcmToken);
                        logger.log('✅ FCM Token registered with backend');
                      } catch (tokenErr) {
                        logger.warn('Failed to register FCM token with backend:', tokenErr);
                      }
                    }

                    Alert.alert(
                      t('common.success') || 'تم بنجاح',
                      t('notifications.enabled') || 'تم تفعيل الإشعارات! ستصلك تنبيهات فورية.',
                      [{ text: t('common.ok') || 'حسناً' }],
                    );
                  } else {
                    logger.log('❌ Notification permissions denied by user');
                    Alert.alert(
                      t('common.info') || 'تنبيه',
                      t('notifications.deniedMessage') ||
                        'يمكنك تفعيل الإشعارات لاحقاً من الإعدادات → الإشعارات',
                      [{ text: t('common.ok') || 'حسناً' }],
                    );
                  }
                } catch (permError) {
                  logger.error('Error requesting Firebase permissions:', permError);
                }
              },
            },
          ],
        );
      });
    } catch (error) {
      logger.error('Error checking notification permissions:', error);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      Alert.alert(t('common.error'), t('login.validation') || 'Please enter email and password');
      return;
    }

    if (!validateEmail(email)) {
      Alert.alert(
        t('common.error'),
        t('login.invalidEmail') || 'Please enter a valid email address',
      );
      return;
    }

    setIsLoading(true);
    // Warp Speed Effect on Loading
    Animated.timing(warpAnim, {
      toValue: 1,
      duration: 3000,
      easing: Easing.bezier(0.25, 1, 0.5, 1), // Fast start then slow
      useNativeDriver: true,
    }).start();

    try {
      // ✅ NEW: Login returns either tempToken (2FA required) or full login
      const loginResult = await login(email, password);

      // ✅ 2FA required - خزّن البيانات في Storage
      if (loginResult?.requires2FA) {
        logger.log('🔐 2FA required - saving session and redirecting');

        // ✅ CRITICAL: Log backend has2FA structure to debug
        logger.log('🔍 [LoginScreen] Backend returned has2FA:', loginResult.has2FA);
        logger.log('🔍 [LoginScreen] has2FA type:', typeof loginResult.has2FA);
        logger.log(
          '🔍 [LoginScreen] has2FA keys:',
          loginResult.has2FA ? Object.keys(loginResult.has2FA) : 'null',
        );
        logger.log('🔍 [LoginScreen] Full loginResult:', JSON.stringify(loginResult, null, 2));

        // ✅ خزّن بيانات 2FA في Storage (عشان ما تضيع مع تغيير Stack)
        await platformStorage.setItem(
          'pending_2fa',
          JSON.stringify({
            tempToken: loginResult.tempToken,
            email: loginResult.email,
            has2FA: loginResult.has2FA,
            createdAt: Date.now(),
          }),
        );

        // ✅ فعّل وضع 2FA (RootNavigator سيحول تلقائياً)
        setPending2FA(true);

        // التحويل للشاشة (RootNavigator بيتحكم)
        navigation.replace('TwoFactorVerify');
        return;
      }

      // No 2FA - login complete
      logger.log('ℹ️ No 2FA required, completing login normally');
      await completeLogin();
    } catch (error: any) {
      // Reset Warp if failed
      Animated.timing(warpAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }).start();
      Alert.alert(t('common.error'), error.message || t('login.failed') || 'Login failed');
      setIsLoading(false);
    }
  };

  const completeLogin = async () => {
    try {
      // Save email only if remember me is checked (security fix: no password storage)
      if (rememberMe) {
        await platformStorage.setItem('saved_email', email);
        await platformStorage.setItem('remember_me', 'true');
        logger.log('Email saved for Remember Me (password not stored for security)');
      } else {
        // Clear saved credentials
        await platformStorage.removeItem('saved_email');
        await platformStorage.removeItem('remember_me');
      }

      setIsLoading(false);

      // ✅ FIX: Defer notification permission request until AFTER navigation completes
      // Presenting Alert during navigation transition causes crash on iOS
      setTimeout(() => {
        requestNotificationPermissions();
      }, 1500);
    } catch (error) {
      logger.error('Error completing login:', error);
      setIsLoading(false);
    }
  };

  return (
    <LinearGradient
      colors={['#050A30', '#0b1654', '#202a7b']}
      style={styles.container}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      {/* Animated Shimmer Background */}
      <Animated.View
        style={[
          styles.shimmerLayer,
          {
            opacity: shimmerAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [0.02, 0.08],
            }),
          },
        ]}
      />

      {/* Nebula Cloud Effect */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.nebulaCloud,
          {
            opacity: nebulaAnim.interpolate({
              inputRange: [0, 0.5, 1],
              outputRange: [0.03, 0.08, 0.03],
            }),
            transform: [
              {
                translateX: nebulaAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-50, 50],
                }),
              },
              {
                scale: nebulaAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [1, 1.2, 1],
                }),
              },
            ],
          },
        ]}
      >
        <LinearGradient
          colors={[
            'rgba(255,100,200,0.3)',
            'rgba(100,150,255,0.3)',
            'rgba(150,255,200,0.2)',
            'transparent',
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.nebulaGradient}
        />
      </Animated.View>

      {/* Floating Particles */}
      <View pointerEvents="none" style={styles.particlesContainer}>
        {particles.map((p) => (
          <Animated.View
            key={`particle-${p.id}`}
            style={[
              styles.particle,
              {
                top: `${p.top}%`,
                left: `${p.left}%`,
                width: p.size,
                height: p.size,
                opacity: particlesAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [p.opacity * 0.5, p.opacity, p.opacity * 0.5],
                }),
                transform: [
                  {
                    translateY: particlesAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0, -30],
                    }),
                  },
                ],
              },
            ]}
          />
        ))}
      </View>

      {/* Galaxy Background Layer */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.galaxyContainer,
          {
            transform: [
              {
                rotate: galaxyRotate.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '360deg'],
                }),
              },
              { scale: warpAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 2.5] }) },
            ],
          },
        ]}
      >
        <View style={styles.galaxyCore} />
        {stars.map((s, idx) => (
          <Animated.View
            key={`s-${s.id}`}
            style={[
              styles.star,
              {
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                borderRadius: s.size / 2,
                opacity: twinkleAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [s.opacity, s.opacity * 0.4, s.opacity],
                }),
              },
            ]}
          />
        ))}
      </Animated.View>

      {/* Floating Planets with Orbit Motion */}
      <View pointerEvents="none" style={styles.planetsContainer}>
        {planets.map((planet, idx) => (
          <Animated.View
            key={`planet-${planet.id}`}
            style={[
              styles.planet,
              {
                top: `${planet.top}%`,
                left: `${planet.left}%`,
                width: planet.size,
                height: planet.size,
                backgroundColor: planet.color,
                borderRadius: planet.size / 2,
                transform: [
                  {
                    rotate: planetsAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', idx % 2 === 0 ? '360deg' : '-360deg'],
                    }),
                  },
                  {
                    translateY: planetsAnim.interpolate({
                      inputRange: [0, 0.5, 1],
                      outputRange: [0, -20, 0],
                    }),
                  },
                  {
                    scale: planetsAnim.interpolate({
                      inputRange: [0, 0.5, 1],
                      outputRange: [1, 1.1, 1],
                    }),
                  },
                ],
                shadowColor: planet.color,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.8,
                shadowRadius: 15,
              },
            ]}
          >
            {/* Planet ring for variety */}
            {idx === 1 && <View style={styles.planetRing} />}
          </Animated.View>
        ))}
      </View>

      {/* Enhanced Shooting Star with Curved Path */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.shootingStar,
          {
            opacity: shootingStarAnim.interpolate({
              inputRange: [0, 0.15, 0.7, 1],
              outputRange: [0, 1, 0.6, 0], // Slower fade
            }),
            transform: [
              // Natural curved trajectory
              {
                translateX: shootingStarAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [
                    Dimensions.get('window').width + 150,
                    Dimensions.get('window').width * 0.3,
                    -250,
                  ], // Curved path
                }),
              },
              {
                translateY: shootingStarAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [
                    -150,
                    Dimensions.get('window').height * 0.35,
                    Dimensions.get('window').height * 0.7,
                  ], // Gentle curve
                }),
              },
              {
                rotate: shootingStarAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['-35deg', '-45deg'], // Slight rotation during flight
                }),
              },
              {
                scale: shootingStarAnim.interpolate({
                  inputRange: [0, 0.3, 1],
                  outputRange: [0.8, 1, 0.6], // Size variation
                }),
              },
            ],
          },
        ]}
      >
        {/* Main shooting star with cosmic colors */}
        <LinearGradient
          colors={[
            'rgba(255,255,255,1)',
            'rgba(150,200,255,0.9)',
            'rgba(100,150,255,0.5)',
            'rgba(255,200,100,0.3)',
            'transparent',
          ]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={{ flex: 1, borderRadius: 2 }}
        />
        {/* Glow effect */}
        <Animated.View
          style={[
            styles.shootingStarGlow,
            {
              opacity: shootingStarAnim.interpolate({
                inputRange: [0, 0.2, 0.8, 1],
                outputRange: [0, 0.8, 0.4, 0],
              }),
            },
          ]}
        />
      </Animated.View>

      {/* Animated Wave Footer - Updated to match Galaxy Theme */}
      <View style={styles.waveFooter}>
        <AnimatedWave height={320} amplitude={30} opacity={0.08} color="#a5f3fc" speed={0.4} />
        <View style={{ marginTop: -220 }}>
          <AnimatedWave
            height={360}
            amplitude={35}
            opacity={0.12}
            color="#4f46e5"
            flip
            speed={0.6}
          />
        </View>
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <View style={styles.contentContainer}>
          {/* Animated Header with Logo */}
          <Animated.View style={[styles.header, { transform: [{ scale: logoScale }] }]}>
            <View style={styles.logoContainer}>
              <Animated.View
                style={[
                  styles.logoGradientBorder,
                  {
                    shadowOpacity: logoPulseAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.5, 0.9],
                    }),
                    transform: [
                      {
                        scale: logoPulseAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: [1, 1.03], // Subtle breathing size
                        }),
                      },
                    ],
                  },
                ]}
              >
                <View style={styles.logoCircle}>
                  <Image
                    source={require('../../assets/logo.png')}
                    style={styles.logoImage}
                    resizeMode="contain"
                  />
                  {/* Online Indicator */}
                  <View style={styles.onlineIndicatorLogo}>
                    <View style={styles.onlineDotLogo} />
                  </View>
                </View>
              </Animated.View>
            </View>

            <Text style={styles.title}>{t('app.name')}</Text>
            <Text style={styles.subtitle}>{t('app.description')}</Text>

            {/* Top Controls: Language & Status */}
            <View style={styles.topControls}>
              <TouchableOpacity
                style={[styles.languageButton, styles.buttonGlow]}
                onPress={toggleLanguage}
                activeOpacity={0.7}
              >
                <View style={styles.iconGlow} />
                <Ionicons name="language" size={18} color={colors.text.inverse} />
                <Text style={styles.languageText}>{currentLanguage === 'ar' ? 'EN' : 'ع'}</Text>
              </TouchableOpacity>

              <View style={[styles.statusIndicator, styles.statusGlow]}>
                <View style={styles.statusDot} />
                <Text style={styles.statusText}>{t('login.online') || 'Online'}</Text>
              </View>
            </View>
          </Animated.View>

          {/* Animated Login Card */}
          <Animated.View
            style={[
              styles.card,
              {
                opacity: cardOpacity,
                transform: [{ translateY: cardSlideUp }],
              },
            ]}
          >
            <Text style={styles.cardTitle}>{t('login.title')}</Text>

            {/* Email Input */}
            <View style={styles.inputWrapper}>
              <Text style={[styles.inputLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                {t('login.email')}
              </Text>
              <View
                style={[
                  styles.inputContainer,
                  styles.inputGlassEffect,
                  focusedInput === 'email' && styles.inputFocused,
                ]}
              >
                <Ionicons
                  name="mail-outline"
                  size={22}
                  color={focusedInput === 'email' ? '#a5f3fc' : colors.primary[500]}
                  style={styles.inputIcon}
                />
                <TextInput
                  style={[styles.input, { textAlign: isRTL ? 'right' : 'left' }]}
                  placeholder={t('login.email_placeholder')}
                  placeholderTextColor={colors.gray[400]}
                  value={email}
                  onChangeText={setEmail}
                  onFocus={() => setFocusedInput('email')}
                  onBlur={() => setFocusedInput(null)}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  editable={!isLoading}
                  accessibilityLabel={t('login.email')}
                  accessibilityHint={t('login.email_placeholder')}
                  accessibilityRole="none"
                />
              </View>
            </View>

            {/* Password Input */}
            <View style={styles.inputWrapper}>
              <Text style={[styles.inputLabel, { textAlign: isRTL ? 'right' : 'left' }]}>
                {t('login.password')}
              </Text>
              <View
                style={[
                  styles.inputContainer,
                  styles.inputGlassEffect,
                  focusedInput === 'password' && styles.inputFocused,
                ]}
              >
                <Ionicons
                  name="lock-closed-outline"
                  size={22}
                  color={focusedInput === 'password' ? '#a5f3fc' : colors.primary[500]}
                  style={styles.inputIcon}
                />
                <TextInput
                  style={[styles.input, { textAlign: isRTL ? 'right' : 'left' }]}
                  placeholder={t('login.password_placeholder')}
                  placeholderTextColor={colors.gray[400]}
                  value={password}
                  onChangeText={setPassword}
                  onFocus={() => setFocusedInput('password')}
                  onBlur={() => setFocusedInput(null)}
                  secureTextEntry={!showPassword}
                  autoComplete="password"
                  editable={!isLoading}
                  accessibilityLabel={t('login.password')}
                  accessibilityHint={t('login.password_placeholder')}
                  accessibilityRole="none"
                />
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  style={styles.eyeIcon}
                  accessible={true}
                  accessibilityLabel={
                    showPassword ? t('login.hide_password') : t('login.show_password')
                  }
                  accessibilityRole="button"
                  accessibilityHint={t('login.toggle_password_visibility')}
                >
                  <Ionicons
                    name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                    size={22}
                    color={colors.gray[400]}
                  />
                </TouchableOpacity>
              </View>
            </View>

            {/* Remember Me */}
            <TouchableOpacity
              style={styles.rememberMeContainer}
              onPress={() => setRememberMe(!rememberMe)}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={t('login.remember_me')}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: rememberMe }}
              accessibilityHint={t('login.remember_me_hint')}
            >
              <View style={[styles.checkbox, rememberMe && styles.checkboxChecked]}>
                {rememberMe && <Ionicons name="checkmark" size={16} color={colors.text.inverse} />}
              </View>
              <Text style={styles.rememberMeText}>{t('login.remember_me')}</Text>
            </TouchableOpacity>

            {/* Login Button with Enhanced Design */}
            <TouchableOpacity
              onPress={handleLogin}
              disabled={isLoading}
              activeOpacity={0.8}
              style={styles.loginButtonWrapper}
              accessible={true}
              accessibilityLabel={t('login.submit')}
              accessibilityRole="button"
              accessibilityState={{ disabled: isLoading, busy: isLoading }}
              accessibilityHint={t('login.submit_hint')}
            >
              <LinearGradient
                colors={[colors.primary[500], colors.primary[700]]}
                style={styles.loginButton}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                {isLoading ? (
                  <ActivityIndicator color={colors.text.inverse} size="small" />
                ) : (
                  <>
                    <Text style={styles.loginButtonText}>{t('login.submit')}</Text>
                    <Ionicons
                      name={currentLanguage === 'ar' ? 'arrow-back' : 'arrow-forward'}
                      size={22}
                      color={colors.text.inverse}
                    />
                  </>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </Animated.View>

          {/* Footer */}
          <View style={styles.footerContainer}>
            <Text style={styles.footer}>
              © 2026 {t('app.name')} • v{appConfig.expo.version}
            </Text>
          </View>
        </View>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  shimmerLayer: {
    position: 'absolute',
    top: -50,
    left: -50,
    right: -50,
    bottom: -50,
    backgroundColor: '#ffffff',
  },
  galaxyContainer: {
    position: 'absolute',
    width: '200%',
    height: '200%',
    top: '-50%',
    left: '-50%',
    zIndex: 0,
  },
  galaxyCore: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 300,
    height: 300,
    marginTop: -150,
    marginLeft: -150,
    borderRadius: 150,
    backgroundColor: 'rgba(100, 150, 255, 0.04)',
    shadowColor: '#80bdff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 60,
  },
  star: {
    position: 'absolute',
    backgroundColor: '#ffffff',
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 3,
    elevation: 3,
  },
  shootingStar: {
    position: 'absolute',
    top: 50,
    width: 250, // Longer tail
    height: 3, // Slightly thicker
    zIndex: 0, // Behind all content (fixed: was covering login inputs)
  },
  shootingStarGlow: {
    position: 'absolute',
    top: -8,
    left: -10,
    right: -10,
    bottom: -8,
    backgroundColor: 'rgba(150,200,255,0.3)',
    borderRadius: 20,
    shadowColor: '#a5f3fc',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 20,
  },
  nebulaCloud: {
    position: 'absolute',
    top: '10%',
    left: '10%',
    width: '80%',
    height: '60%',
    zIndex: 1, // Behind everything
  },
  nebulaGradient: {
    flex: 1,
    borderRadius: 200,
  },
  particlesContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1, // Behind card
  },
  particle: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderRadius: 50,
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  planetsContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2, // Behind card
  },
  planet: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.2)',
    elevation: 5,
  },
  planetRing: {
    position: 'absolute',
    top: '30%',
    left: '-20%',
    right: '-20%',
    height: 2,
    backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 10,
    transform: [{ rotateX: '60deg' }],
  },
  waveFooter: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 320,
    overflow: 'hidden',
  },
  floatingDot: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 6,
  },
  keyboardView: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl,
    paddingBottom: spacing.md,
    justifyContent: 'center',
    zIndex: 10, // Above all background effects
    position: 'relative',
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.lg,
    zIndex: 10, // Above all background effects
  },
  logoContainer: {
    marginBottom: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoGradientBorder: {
    padding: 4,
    borderRadius: Platform.OS === 'web' ? 77 : 67,
    backgroundColor: '#64c8ff',
    shadowColor: '#64c8ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.7,
    shadowRadius: 15,
    elevation: 8,
  },
  logoCircle: {
    width: Platform.OS === 'web' ? 150 : 130,
    height: Platform.OS === 'web' ? 150 : 130,
    borderRadius: Platform.OS === 'web' ? 75 : 65,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.5)',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  logoImage: {
    width: Platform.OS === 'web' ? 110 : 95,
    height: Platform.OS === 'web' ? 110 : 95,
  },
  onlineIndicatorLogo: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 2,
  },
  onlineDotLogo: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#4ade80',
    shadowColor: '#4ade80',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 6,
  },
  title: {
    fontSize: Platform.OS === 'web' ? 28 : 24,
    fontWeight: '800',
    color: colors.text.inverse,
    marginBottom: spacing.xs,
    textShadowColor: 'rgba(0,0,0,0.4)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    marginBottom: spacing.sm,
    fontWeight: '500',
  },
  topControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  buttonGlow: {
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 10,
    elevation: 4,
  },
  iconGlow: {
    position: 'absolute',
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  statusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(34, 197, 94, 0.25)',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.full,
    borderWidth: 1.5,
    borderColor: 'rgba(34, 197, 94, 0.6)',
    gap: spacing.xs,
  },
  statusGlow: {
    shadowColor: '#22c55e',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 8,
    elevation: 3,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.success[400],
    shadowColor: colors.success[400],
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.text.inverse,
  },
  languageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.full,
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    position: 'relative',
  },
  languageText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.text.inverse,
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderRadius: borderRadius.xxl,
    padding: Platform.OS === 'web' ? spacing.xl : spacing.lg,
    marginBottom: spacing.md,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.5)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.4,
    shadowRadius: 20,
    elevation: 12,
    zIndex: 10, // Above all background effects
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: spacing.lg,
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  inputWrapper: {
    marginBottom: spacing.md,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: borderRadius.lg,
    borderWidth: 2,
    paddingHorizontal: spacing.md,
    height: 50,
  },
  inputGlassEffect: {
    backgroundColor: 'rgba(100, 200, 255, 0.08)',
    borderColor: 'rgba(100, 200, 255, 0.3)',
    shadowColor: '#64c8ff',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  inputFocused: {
    backgroundColor: 'rgba(165, 243, 252, 0.15)',
    borderColor: '#a5f3fc',
    shadowColor: '#a5f3fc',
    shadowOpacity: 0.6,
    shadowRadius: 8,
    elevation: 10,
  },
  inputIcon: {
    marginRight: spacing.sm,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: colors.text.primary,
    fontWeight: '500',
  },
  eyeIcon: {
    padding: spacing.xs,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.gray[300],
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
    shadowColor: colors.gray[300],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 2,
  },
  checkboxChecked: {
    backgroundColor: colors.primary[500],
    borderColor: colors.primary[500],
    shadowColor: colors.primary[500],
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 4,
  },
  rememberMeText: {
    fontSize: 13,
    color: colors.gray[600],
    fontWeight: '500',
  },
  loginButtonWrapper: {
    marginTop: spacing.sm,
  },
  loginButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 50,
    borderRadius: borderRadius.lg,
    shadowColor: colors.primary[500],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 8,
  },
  loginButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text.inverse,
    marginRight: spacing.sm,
  },
  footerContainer: {
    alignItems: 'center',
    marginTop: spacing.md,
  },
  footer: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    fontWeight: '500',
    letterSpacing: 0.3,
  },
});
